// The review screen's camera control: a single circular **recentre-on-the-hive** button.
//
// It was a four-control zoom rail (reset, +, a level readout, −) until WAG-93. The rail existed
// as "the pointer-device fallback for pinch" at a time when the gesture adapter could not
// actually pinch — it tracked one pointer, so a second finger landing became a new drag. Now
// that two-finger pinch is real (`use-canvas-camera-gestures.ts`), the fallback is buttons
// duplicating a gesture the phone already has, taking up the right edge of a small screen to do
// it: on touch you pinch, on a mouse you wheel. What no gesture expresses is "put me back where
// the run opened", so that is the one control that stays.
//
// Deliberately NOT the Match screen's `CameraControl`: that one carries a read-only LOD
// **tier chip**, and a trace's tier is a residency fact about the file, not a function of
// zoom — the review screen already reports it honestly through `TierIndicator`
// (UI.md §6: "Detail must not be colour-privileged over Coarse"). Showing a second,
// zoom-derived tier badge beside it would contradict the real one the moment a player
// zoomed out over a resident detail range.
//
// Purely presentational: the screen owns the controlled `{centre, zoom}` state.

import type { CSSProperties } from 'react';

export interface ReviewCameraControlProps {
  /** The current controlled zoom (scale). No longer drawn — the rail's level readout went with
   * the rail — but still reported on the control's root as `data-zoom`, so the zoom the control
   * governs stays observable to a test and to anyone inspecting a live screen. */
  zoom: number;
  /** Recentre on the colony at the opening colony-view zoom. */
  onReset: () => void;
}

/** 44 px, the platform minimum touch target, and fully round — the shape states that it is one
 * button rather than the top segment of a stack. */
const HOME_BTN: CSSProperties = {
  width: 44,
  height: 44,
  minHeight: 44,
  borderRadius: 999,
  border: '1.5px solid var(--hud-line)',
  background: 'var(--hud-bg)',
  boxShadow: 'var(--shadow-hud)',
  fontFamily: 'var(--font-display)',
  fontWeight: 600,
  fontSize: 18,
  color: 'var(--hud-text)',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
};

export function ReviewCameraControl({ zoom, onReset }: ReviewCameraControlProps) {
  return (
    <div
      data-testid="review-camera-control"
      data-gesture-chrome=""
      data-zoom={zoom.toFixed(2)}
      style={{
        position: 'absolute',
        // Clear of the run rail (bottom-left) and the transport (bottom-centre).
        right: 14,
        top: '50%',
        transform: 'translateY(-50%)',
        display: 'flex',
      }}
    >
      <button
        type="button"
        data-testid="review-camera-reset"
        title="Recentre on the hive"
        aria-label="Recentre on the hive"
        onClick={onReset}
        style={HOME_BTN}
      >
        ⌂
      </button>
    </div>
  );
}
